import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { useState, useRef } from 'react';
import { useThree } from '@react-three/fiber';

// BlockGrid — the ground grid the building editor works on.
//
// THE CELL IS A STREET CORNER, not a block. One intersection square = 1x1, so a
// block plus its streets is roughly six cells across. That is deliberately fine:
// a block-sized cell can only place blocks, and the point of this is to fill a
// city with pieces smaller than the thing you are filling.
//
// Drawn from the model's own footprint (cityBounds) rather than a fixed extent,
// so the grid covers exactly the map and its origin never drifts from the city.
//
// CALIBRATION FIRST. The exact world size of a corner is a property of the model,
// not something to derive from a screenshot — so the size is live-tunable and
// every tenth line is emphasised, which is what makes a mismatch obvious at a
// glance. Line up the emphasis with the streets, then bake the value here.
//
// Tune live: ?cell (world units per cell) · ?gridox ?gridoz (origin nudge, world
// units — a half-cell drift is an ORIGIN problem, not a size one) · ?gridy.
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};

const MINOR = '#3d4756';
const MAJOR = '#8fa6c4';

export default function BlockGrid() {
  const ON = useWorldStore((s) => s.adminTool) === 'blocks';
  const bounds = useWorldStore((s) => s.cityBounds);
  // The brush: whatever ?pick=1 last identified. A mesh NAME is the whole brush —
  // PlacedBuildings clones it out of the model on render.
  const picked = useWorldStore((s) => s.picked);
  const addObj = useWorldStore((s) => s.addWorldObject);
  const setPicked = useWorldStore((s) => s.setPicked);
  const setBlockLast = useWorldStore((s) => s.setBlockLast);
  const brushRot = useWorldStore((s) => s.blockRot);
  const [hover, setHover] = useState(null);
  const [armed, setArmed] = useState(false);   // paint modifier is held
  const down = useRef(null);                  // where the pointer went down

  const geom = useMemo(() => {
    if (!bounds) return null;
    const cell = Math.max(0.5, qp('cell', 5));
    // ORIGIN NUDGE. The lattice snaps to whole cells through the WORLD origin,
    // but the model's streets have no reason to be aligned to it — the city was
    // centred on 0 by its own bounding box, not by its road grid. Any constant
    // mismatch therefore shows up as a uniform half-cell drift, which is exactly
    // what a wrong cell SIZE does not look like: a wrong size drifts more the
    // further you travel, a wrong origin is off by the same amount everywhere.
    // So this is a separate control, not a fudge to the size.
    const ox = qp('gridox', 0);
    const oz = qp('gridoz', 0);
    const [x0, z0] = bounds.min;
    const [x1, z1] = bounds.max;
    const sx = Math.ceil((x0 - ox) / cell) * cell + ox;
    const sz = Math.ceil((z0 - oz) / cell) * cell + oz;

    const minor = [];
    const major = [];
    for (let x = sx; x <= x1; x += cell) {
      const idx = Math.round((x - ox) / cell);
      (idx % 10 === 0 ? major : minor).push(x, 0, z0, x, 0, z1);
    }
    for (let z = sz; z <= z1; z += cell) {
      const idx = Math.round((z - oz) / cell);
      (idx % 10 === 0 ? major : minor).push(x0, 0, z, x1, 0, z);
    }
    const make = (arr) => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3));
      return g;
    };
    return { minor: make(minor), major: make(major), cell, ox, oz, cols: Math.round((x1 - x0) / cell), rows: Math.round((z1 - z0) / cell) };
  }, [bounds]);

  useEffect(() => {
    if (!geom) return undefined;
    console.log(`[grid] cell ${geom.cell} → ${geom.cols} x ${geom.rows} cells · origin nudge ${geom.ox}, ${geom.oz}. ?cell= ?gridox= ?gridoz=`);
    return () => { geom.minor.dispose(); geom.major.dispose(); };
  }, [geom]);

  // NO GEOMETRY IN THE RAY PATH.
  //
  // The first version hung the interaction on an invisible plane at ground
  // level, which meant the pointer had to REACH it — and sidewalks, kerbs and
  // building lots all stand higher than it does. The ray stopped on them, so the
  // only cells you could click were the bare road: precisely inverted from where
  // buildings go.
  //
  // Intersecting a mathematical plane ignores the scene entirely. Every point on
  // the map is reachable whatever is standing on it, which is what a plan-view
  // editor needs — you are working on the ground, not on the things already
  // sitting on it.
  const { camera, gl } = useThree();
  const plane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0));
  const ray = useRef(new THREE.Raycaster());
  const hit = useRef(new THREE.Vector3());
  const ndc = useRef(new THREE.Vector2());
  // Latest values for the DOM listener, which is bound once and would otherwise
  // capture the first render's geom/brush forever.
  const live = useRef({});
  live.current = { geom, picked, brushRot, addObj, setBlockLast };

  // Snapping to the CENTRE of a cell (not its corner) is what makes a pasted
  // building sit in the square rather than straddle two of them.
  const cellAt = (point, g) => {
    const gx = Math.floor((point.x - g.ox) / g.cell);
    const gz = Math.floor((point.z - g.oz) / g.cell);
    return { gx, gz, x: g.ox + (gx + 0.5) * g.cell, z: g.oz + (gz + 0.5) * g.cell };
  };

  const cellUnderPointer = (ev) => {
    const g = live.current.geom;
    if (!g) return null;
    const r = gl.domElement.getBoundingClientRect();
    ndc.current.set(
      ((ev.clientX - r.left) / r.width) * 2 - 1,
      -((ev.clientY - r.top) / r.height) * 2 + 1,
    );
    ray.current.setFromCamera(ndc.current, camera);
    if (!ray.current.ray.intersectPlane(plane.current, hit.current)) return null;
    return cellAt(hit.current, g);
  };

  useEffect(() => {
    if (!ON) return undefined;
    const el = gl.domElement;
    const onMove = (ev) => {
      setHover(cellUnderPointer(ev));
      // The highlight only reads as "this click will paint" while the modifier
      // is actually held, so the armed state is visible rather than remembered.
      setArmed(ev.shiftKey);
    };
    // A look-drag ends in a click. Without this, swinging the camera around with
    // the modifier held would drop a building wherever you let go.
    const onDown = (ev) => { down.current = { x: ev.clientX, y: ev.clientY }; };
    const onClick = (ev) => {
      const { picked: p, brushRot: rot, addObj: add, setBlockLast: report } = live.current;
      // SHIFT IS THE CHORD, and the brush decides what it means:
      //   no brush  → shift-click PICKS the building under the cursor
      //   brush     → shift-click PAINTS a copy on the cell
      // One modifier, two phases, and a plain click never does anything — which
      // is the point, because looking around and placing used to be the same
      // gesture and every camera move dropped a building.
      // Esc drops the brush, which is how you get back to picking.
      if (!ev.shiftKey) { report({ ok: false, why: 'hold Shift to paint' }); return; }
      if (!p?.name) return;   // no brush: CityModel handles this click as a pick
      const d0 = down.current;
      if (d0 && Math.hypot(ev.clientX - d0.x, ev.clientY - d0.y) > 4) {
        report({ ok: false, why: 'that was a drag, not a click' });
        return;
      }
      if (!p?.name) { report({ ok: false, why: 'no brush — shift-click a building first' }); return; }
      const c = cellUnderPointer(ev);
      if (!c) { report({ ok: false, why: 'pointer missed the ground plane' }); return; }
      add({
        zone_id: 'street', kind: 'building', ref: p.name,
        slot: null, grid: [c.gx, c.gz], pos: [+c.x.toFixed(2), 0, +c.z.toFixed(2)],
        rotY: rot, size: 1,
      });
      report({ gx: c.gx, gz: c.gz, x: +c.x.toFixed(1), z: +c.z.toFixed(1), ok: true, why: p.name });
    };
    // Escape drops the brush. Without a way to put it down, every click on the
    // map is a paste — there is no neutral state in which you can look around,
    // and the cell highlight stays armed green whether you meant it or not.
    // Deliberately not stopping propagation: Escape closes other things too, and
    // this is one more listener rather than a claim on the key.
    const onKey = (ev) => {
      if (ev.key !== 'Escape') return;
      setPicked(null);
      setHover(null);
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
      setHover(null);
    };
  }, [ON, gl, camera]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ON || !geom) return null;
  const y = qp('gridy', 0.06);   // just clear of the road so it doesn't z-fight
  const { cell } = geom;

  return (
    <group position={[0, y, 0]}>
      {/* The cell under the cursor. Green once there is something to paste,
          amber when there is not — so "nothing happens on click" is visible
          before you click rather than after. */}
      {hover && (
        // DRAWN ON TOP. The highlight sits just above ground level, and kerbs,
        // sidewalks and lots all stand higher — so it was being computed for
        // every cell and then hidden behind whatever was standing on it, which
        // looked exactly like it only worked over the road. depthTest off (with
        // a late renderOrder) means the cell you are pointing at is always the
        // cell you can see.
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[hover.x, 0.01, hover.z]} renderOrder={999}>
          <planeGeometry args={[cell, cell]} />
          <meshBasicMaterial
            color={picked?.name && armed ? '#9dff7d' : '#ffb25e'}
            transparent opacity={picked?.name && armed ? 0.5 : 0.22} depthTest={false} depthWrite={false} fog={false}
          />
        </mesh>
      )}

      <lineSegments geometry={geom.minor} renderOrder={998}>
        <lineBasicMaterial color={MINOR} transparent opacity={0.5} fog={false} depthTest={false} depthWrite={false} />
      </lineSegments>
      <lineSegments geometry={geom.major} renderOrder={998}>
        <lineBasicMaterial color={MAJOR} transparent opacity={0.85} fog={false} depthTest={false} depthWrite={false} />
      </lineSegments>
    </group>
  );
}
